import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';
import type { MentionMember, MentionSuggestion, MentionSuggestionsSource } from '@shared/dream-components';
import { useQueryClient } from '@tanstack/react-query';

import type { loader as communityLoader } from '~/routes/community';
import type { MemberSuggestionsResponse } from '~/services/api/community/getMemberSuggestions.server';
import toast from '~/utils/toast';

export const MEMBER_SUGGESTIONS_PATH = '/community/member_suggestions';

const SUGGESTIONS_STALE_TIME_MS = 30 * 1000;

interface UseMentionSuggestionsParams {
  spaceSlug?: string;
  context?: 'post';
}

// Only inside the community layout: the website post page has no flag, so its comments can't add mentions.
const useMentionSuggestions = ({
  spaceSlug,
  context,
}: UseMentionSuggestionsParams): MentionSuggestionsSource | undefined => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const mentionsEnabled = Boolean(layoutData?.community?.mentions_enabled);

  return useMemo(() => {
    if (!mentionsEnabled) return undefined;

    return {
      searchMembers: (query: string) =>
        queryClient.fetchQuery({
          queryKey: ['community-mention-suggestions', spaceSlug, context, query],
          staleTime: SUGGESTIONS_STALE_TIME_MS,
          // The list stays closed on failure, and the next keystroke searches again.
          retry: false,
          queryFn: async ({ signal }): Promise<MentionSuggestion[]> => {
            const params = new URLSearchParams({ q: query });
            if (spaceSlug) params.set('space_slug', spaceSlug);
            if (context) params.set('context', context);
            const response = await fetch(`${MEMBER_SUGGESTIONS_PATH}?${params}`, { signal });
            if (!response.ok) throw new Error('Failed to fetch member suggestions');

            const data: MemberSuggestionsResponse = await response.json();
            return data.suggestions ?? data.members.map((member): MentionMember => ({ ...member, type: 'member' }));
          },
        }),
      onLimitReached: () => toast.error(t('community.mentions.limit_reached')),
    };
  }, [mentionsEnabled, spaceSlug, context, queryClient, t]);
};

export default useMentionSuggestions;
